import React, { useState } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { FiCalendar } from 'react-icons/fi'
import {
  createAppointmentForOrder,
  formatOfflineOrderError,
} from '../../utils/offlineOrders'
import { SuccessNotification, ErrorNotification } from '../../utils'

const CreateOfflineAppointmentModal = ({
  show,
  setShow,
  order,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    date: '',
    time: '',
    message: '',
  })

  if (!show || !order) return null

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.date || !form.time) {
      ErrorNotification('Please select an appointment date and time.')
      return
    }

    setLoading(true)
    try {
      await createAppointmentForOrder(order.id, {
        date: form.date,
        time: form.time,
        message: form.message,
      })

      SuccessNotification(
        `Appointment scheduled. SMS sent to ${order.customer_phone}.`
      )
      setShow(false)
      setForm({ date: '', time: '', message: '' })
      onSuccess?.()
    } catch (err) {
      ErrorNotification(formatOfflineOrderError(err))
    } finally {
      setLoading(false)
    }
  }

  const defaultMessage = order.item_type === 'service'
    ? `Hello ${order.customer_name}, your appointment for "${order.product_name}" is confirmed.`
    : `Hello ${order.customer_name}, your order for "${order.product_name}" follow-up is scheduled.`

  const inputClass =
    'w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md p-4">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full max-w-md p-6 relative">
        <button
          type="button"
          onClick={() => setShow(false)}
          disabled={loading}
          className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700"
        >
          <AiOutlineCloseCircle className="h-6 w-6" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-yellowcolor/20 rounded-full flex items-center justify-center">
            <FiCalendar className="text-yellowcolor" size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold dark:text-white">Schedule Appointment</h2>
            <p className="text-xs font-light text-gray-500 dark:text-gray-400">
              {order.order_number} · {order.customer_name}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">
              Appointment Date<span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">
              Appointment Time<span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              name="time"
              value={form.time}
              onChange={handleChange}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">
              SMS Message
            </label>
            <textarea
              name="message"
              rows={4}
              value={form.message}
              onChange={handleChange}
              placeholder={defaultMessage}
              className={inputClass}
            />
            <p className="text-xs font-light text-gray-500 mt-1 dark:text-gray-400">
              Customer will receive an SMS with the appointment details.
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShow(false)}
              disabled={loading}
              className="w-full sm:flex-1 py-2 text-sm rounded-lg border dark:border-border-dark dark:text-white font-light"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:flex-1 py-2 text-sm rounded-lg bg-yellowcolor hover:bg-yellow-400 text-black disabled:opacity-60"
            >
              {loading ? 'Scheduling...' : 'Schedule & Send SMS'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateOfflineAppointmentModal
